/**
 * Разметка сводного списка: момент выгрузки и раскладка по проектам.
 *
 * Формат периода, статусов и балла берём общий с хранилищем и отчётом —
 * иначе один и тот же отчёт называется в двух списках по-разному.
 *
 * @format
 */

import {MONTHS_GENITIVE} from '../ExecutiveReport/helpers';
import type {OverviewReport} from './types';

/** Формы для счётчика отчётов в заголовке группы. */
export const REPORTS: [string, string, string] = ['отчёт', 'отчёта', 'отчётов'];

/**
 * Момент выгрузки словами: `2026-08-03T14:05:00` → `3 августа, 14:05`.
 *
 * Разбираем строку сами, а не через `Date`: ISO без часового пояса браузер
 * читает как локальное время, с `Z` — как UTC и сдвигает на часовой пояс
 * читателя. Сервер присылает момент по своим часам, и сдвигать его к
 * часовому поясу ноутбука значит показывать не то время, когда отчёт
 * собрался. Непригодный вход даёт `null` — выдуманный момент хуже, чем
 * никакого.
 */
export function generatedMoment(generatedAt: string | undefined): string | null {
  if (!generatedAt) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(generatedAt.trim());
  if (!match) return null;
  const [, , month, day, hours, minutes] = match;
  const monthName = MONTHS_GENITIVE[Number(month) - 1];
  if (!monthName) return null;
  return `${Number(day)} ${monthName}, ${hours}:${minutes}`;
}

export interface ReportsGroup {
  companyId: string;
  companyName: string;
  reports: OverviewReport[];
}

/**
 * Раскладка по проектам с сохранением порядка.
 *
 * Группы идут в порядке первого появления проекта, а он задан свежестью:
 * значит сверху проект, по которому отчёт собрался последним. Сортировать
 * группы по алфавиту было бы честнее для поиска глазами, но список читают
 * ради нового, а не ради буквы «А».
 */
export function groupByCompany(reports: OverviewReport[]): ReportsGroup[] {
  const groups = new Map<string, ReportsGroup>();

  for (const report of reports) {
    const group = groups.get(report.companyId);
    if (group) {
      group.reports.push(report);
      continue;
    }
    groups.set(report.companyId, {
      companyId: report.companyId,
      companyName: report.companyName,
      reports: [report],
    });
  }

  return [...groups.values()];
}
